import { selectFtpForHistoricalRide, type HistoricalFtpSelectionResult, inferRideSurfaceType } from "../athlete-profile/ftpSelection";
import type { AthleteProfile } from "../athlete-profile/types/athleteProfile";
import type { RideParserDiagnostics } from "../ride-center/services/rideUploadPipeline";
import type { Ride } from "../ride-center/types/ride";
import {
  computeStableSourceHash,
  deleteCompletedActivityHistoryRecord,
  saveCompletedActivityRecord,
  type CompletedActivityRecord,
  type DuplicateIdentityResult,
  type HistoricalSourceType,
  type ImportMetadata,
  type SourceProvenanceEntry
} from "./completedActivityHistory";
import type { PrimaryRideClass } from "./sessionAnalysis";
import {
  createHistoricalFitImportWorkerClient,
  HistoricalFitImportWorkerCancelledError,
  HistoricalFitImportWorkerClientError,
  type HistoricalFitImportWorkerClient,
  type HistoricalFitWorkerDiagnostics
} from "./workers/historicalFitWorkerClient";
import type {
  HistoricalFitImportPerformanceDiagnostics,
  HistoricalFitImportWorkerCounts,
  HistoricalFitImportWorkerInternalTimings,
  HistoricalFitImportWorkerProgress,
  HistoricalFitImportWorkerStage,
  HistoricalFitImportWorkerStageTimings
} from "./workers/historicalFitWorkerProtocol";
import { epochNowMs, type WorkerHandoffMarks, type WorkerHandoffProfile } from "./workers/workerHandoffProfiling";

export type HistoricalRideImportRow = {
  fileName: string;
  fileSizeBytes: number;
  recognized: boolean;
  sourceType: HistoricalSourceType;
  detectedRideDate: string | null;
  sport: string | null;
  durationSeconds: number | null;
  distanceMeters: number | null;
  parseStatus: "pending" | "parsed" | "rejected" | "failed" | "cancelled";
  duplicateStatus: "not_checked" | "not_duplicate" | "duplicate" | "canonical_match" | "uncertain";
  persistenceStatus: "pending" | "saved" | "failed" | "skipped_duplicate" | "cancelled";
  failureType: "none" | "cancelled" | "parse_failed" | "persistence_failed";
  message: string | null;
  duplicateIdentity: DuplicateIdentityResult | null;
};

export type HistoricalRideImportSummary = {
  filesScanned: number;
  filesRecognized: number;
  filesRejected: number;
  duplicatesDetected: number;
  importsCompleted: number;
  importsFailed: number;
  ridesCreated: number;
  canonicalMatches: number;
  provenanceMerges: number;
  failedParses: number;
  cancelled: number;
  persistenceFailures: number;
};

export type HistoricalRideImportResult = {
  rows: HistoricalRideImportRow[];
  summary: HistoricalRideImportSummary;
  performanceDiagnostics?: HistoricalFitImportPerformanceDiagnostics | null;
};

export type HistoricalRideImportProgress = {
  currentFileIndex: number;
  totalFiles: number;
  percent: number;
  stage: HistoricalFitImportWorkerStage | "finalizing" | "persisting" | "cancelled";
  status: string;
  workerDiagnostics: HistoricalFitWorkerDiagnostics;
  rows: HistoricalRideImportRow[];
  row: HistoricalRideImportRow;
  summary: HistoricalRideImportSummary;
};

type ImportOptions = {
  signal?: AbortSignal;
  selectionReceivedAtEpochMs?: number;
  onProgress?: (progress: HistoricalRideImportProgress) => void;
  createWorkerClient?: () => HistoricalFitImportWorkerClient;
  persistRecord?: typeof saveCompletedActivityRecord;
  deleteRecord?: typeof deleteCompletedActivityHistoryRecord;
  onBeforePersist?: (input: { file: File; row: HistoricalRideImportRow }) => void | Promise<void>;
};

export class HistoricalRideImportCancelledError extends Error {
  constructor(public readonly partialResult: HistoricalRideImportResult) {
    super("Historical import cancelled.");
    this.name = "HistoricalRideImportCancelledError";
  }
}

function isAbortLikeError(error: unknown): boolean {
  if (!error) return false;
  if (error instanceof Error) {
    return /cancelled|aborted/i.test(error.message);
  }
  return false;
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw new Error("Historical import cancelled.");
  }
}

function isWorkerCancelledError(error: unknown): boolean {
  return error instanceof HistoricalFitImportWorkerCancelledError;
}

async function yieldToUi(): Promise<void> {
  await new Promise<void>(resolve => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(() => finish());
    }
    setTimeout(() => finish(), 16);
  });
}

function computeOverallPercent(fileIndex: number, totalFiles: number, filePercent: number): number {
  const safeTotal = Math.max(1, totalFiles);
  const bounded = Math.max(0, Math.min(100, filePercent));
  const base = (fileIndex / safeTotal) * 100;
  const spread = (bounded / 100) * (100 / safeTotal);
  return Math.round(Math.min(100, base + spread));
}

function toFilePercent(stage: HistoricalFitImportWorkerStage | "finalizing" | "persisting", stagePercent: number | null): number {
  if (typeof stagePercent === "number" && Number.isFinite(stagePercent)) {
    const boundedPercent = Math.max(0, Math.min(100, Math.round(stagePercent)));
    if (stage === "complete") {
      return 72;
    }
    if (stage === "analyzing") {
      return Math.max(82, boundedPercent);
    }
    if (stage === "finalizing") {
      return Math.max(88, boundedPercent);
    }
    if (stage === "persisting") {
      return Math.max(92, boundedPercent);
    }
    return boundedPercent;
  }

  switch (stage) {
    case "queued":
      return 5;
    case "reading":
      return 10;
    case "validating":
      return 17;
    case "decompressing":
      return 26;
    case "parsing":
      return 37;
    case "normalizing":
      return 52;
    case "analyzing":
      return 82;
    case "complete":
      return 72;
    case "finalizing":
      return 88;
    case "persisting":
      return 92;
  }
}

function isRecognizedRideFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return name.endsWith(".fit") || name.endsWith(".fit.gz");
}

function sourceTypeFromFileName(fileName: string): HistoricalSourceType {
  const normalized = fileName.toLowerCase();
  if (normalized.endsWith(".fit.gz") && normalized.includes("trainingpeaks")) return "TrainingPeaks FIT.gz";
  if (normalized.endsWith(".fit.gz")) return "TrainingPeaks FIT.gz";
  if (normalized.includes("trainingpeaks")) return "TrainingPeaks FIT";
  if (normalized.endsWith(".fit")) return "Garmin FIT";
  return "unknown";
}

function toIso(value: Ride["rideDate"]): string | null {
  if (typeof value === "string") {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : new Date(parsed).toISOString();
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return new Date(value).toISOString();
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString();
  }
  return null;
}

function safeNumber(value: number | null | undefined): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function average(values: Array<number | null | undefined>): number | null {
  const filtered = values.filter((value): value is number => typeof value === "number" && Number.isFinite(value));
  if (filtered.length === 0) return null;
  return filtered.reduce((sum, value) => sum + value, 0) / filtered.length;
}

function halfMetrics(ride: Ride): { firstHalfPower: number | null; secondHalfPower: number | null; firstHalfHeartRate: number | null; secondHalfHeartRate: number | null } {
  if (ride.records.length < 2) {
    return {
      firstHalfPower: null,
      secondHalfPower: null,
      firstHalfHeartRate: null,
      secondHalfHeartRate: null
    };
  }

  const midpoint = Math.floor(ride.records.length / 2);
  const first = ride.records.slice(0, midpoint);
  const second = ride.records.slice(midpoint);

  return {
    firstHalfPower: average(first.map(record => safeNumber(record.powerWatts))),
    secondHalfPower: average(second.map(record => safeNumber(record.powerWatts))),
    firstHalfHeartRate: average(first.map(record => safeNumber(record.heartRateBpm))),
    secondHalfHeartRate: average(second.map(record => safeNumber(record.heartRateBpm)))
  };
}

function mapWorkoutTypeToPrimaryClass(ride: Ride): PrimaryRideClass {
  const workoutType = ride.rideAnalytics?.workoutClassification.workoutType;
  switch (workoutType) {
    case "recovery":
      return "recovery";
    case "endurance":
      return "endurance";
    case "tempo":
      return "tempo";
    case "sweet-spot":
      return "sweet_spot";
    case "threshold":
      return "threshold";
    case "vo2max":
      return "vo2";
    case "sprint":
    case "anaerobic":
      return "sprint";
    case "mixed":
      return "mixed";
    default:
      return "unknown";
  }
}

function detectSport(diagnostics: RideParserDiagnostics): string {
  return diagnostics.detectedSport || "cycling";
}

function sourceActivityIdFromFileName(fileName: string): string | null {
  const match = fileName.match(/(\d{8,})/);
  return match?.[1] ?? null;
}

function buildSourceProvenance(input: {
  sourceKey: string;
  sourceType: HistoricalSourceType;
  fileName: string;
  fileSizeBytes: number;
  sourceActivityId: string | null;
  confidence: "high" | "medium" | "low";
  importedAt: string;
}): SourceProvenanceEntry[] {
  return [{
    sourceKey: input.sourceKey,
    sourceType: input.sourceType,
    sourceFileName: input.fileName,
    sourceFileSizeBytes: input.fileSizeBytes,
    sourceActivityId: input.sourceActivityId,
    importedAt: input.importedAt,
    importMode: "historical_batch_analysis",
    confidence: input.confidence
  }];
}

function buildImportMetadata(input: {
  sourceType: HistoricalSourceType;
  fileName: string;
  sourceActivityId: string | null;
  importedAt: string;
  ftp: HistoricalFtpSelectionResult;
  confidence: "high" | "medium" | "low";
}): ImportMetadata {
  return {
    importedAt: input.importedAt,
    importMode: "historical_batch_analysis",
    sourceType: input.sourceType,
    sourceFileName: input.fileName,
    sourceActivityId: input.sourceActivityId,
    provenance: "historical_batch_analysis",
    confidence: input.confidence,
    ftpResolution: input.ftp.historicalPolicy,
    historicalFtpFallbackApplied: input.ftp.fallbackUsed
  };
}

function buildHistoricalRecord(input: {
  ride: Ride;
  ftp: HistoricalFtpSelectionResult;
  file: File;
  sourceType: HistoricalSourceType;
}): CompletedActivityRecord {
  const rideDateIso = toIso(input.ride.rideDate) ?? new Date().toISOString();
  const startTimestampMs = Date.parse(rideDateIso);
  const durationSeconds = safeNumber(input.ride.elapsedTime ?? input.ride.movingTime ?? null);
  const distanceMeters = safeNumber(input.ride.distance);
  const sourceActivityId = sourceActivityIdFromFileName(input.file.name);
  const sourceKey = computeStableSourceHash({
    sourceFingerprint: `${input.file.name.toLowerCase()}::${input.file.size}::${input.file.lastModified}`,
    sourceFileName: input.file.name,
    sourceFileSizeBytes: input.file.size,
    startTimestampMs: Number.isNaN(startTimestampMs) ? null : startTimestampMs,
    durationSeconds,
    distanceMeters
  });

  const half = halfMetrics(input.ride);
  const confidence: "high" | "medium" | "low" = input.ftp.fallbackUsed ? "medium" : "high";
  const importedAt = new Date().toISOString();
  const rideClass = mapWorkoutTypeToPrimaryClass(input.ride);
  const displayName = input.ride.rideAnalytics?.workoutClassification.workoutName ?? "Historical Ride";

  return {
    schemaVersion: 1,
    activityId: `historical:${sourceKey}`,
    activityDate: rideDateIso,
    activityTitle: `${displayName} · ${new Date(rideDateIso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`,
    activityType: "cycling",
    rideClassification: {
      type: rideClass,
      displayName,
      confidencePercent: input.ride.rideAnalytics?.workoutClassification.confidence
        ? Math.round(input.ride.rideAnalytics.workoutClassification.confidence * 100)
        : 70,
      structured: Boolean(input.ride.rideAnalytics?.workoutClassification.structuredSetCount)
    },
    activityContext: {
      indoorOutdoor: input.ride.records.some(record => safeNumber(record.latitudeDegrees) !== null && safeNumber(record.longitudeDegrees) !== null) ? "outdoor" : "indoor",
      surface: inferRideSurfaceType(input.file.name)
    },
    durationSeconds,
    distanceMeters,
    elevationGainMeters: safeNumber(input.ride.elevationGain),
    movingTimeSeconds: safeNumber(input.ride.movingTime),
    elapsedTimeSeconds: safeNumber(input.ride.elapsedTime),
    ftpSnapshot: {
      ftpWatts: input.ftp.ftpWatts,
      sourceLabel: input.ftp.sourceLabel,
      effectiveDateIso: input.ftp.effectiveDateIso,
      policyKind: input.ftp.selectedPolicy.kind
    },
    averagePower: safeNumber(input.ride.averagePower ?? input.ride.rideAnalytics?.averagePower ?? null),
    normalizedPower: safeNumber(input.ride.rideAnalytics?.normalizedPower ?? input.ride.normalizedPower ?? null),
    intensityFactor: safeNumber(input.ride.rideAnalytics?.intensityFactor ?? input.ride.if ?? null),
    trainingStressScore: safeNumber(input.ride.rideAnalytics?.trainingStressScore ?? input.ride.tss ?? null),
    variabilityIndex: safeNumber(input.ride.rideAnalytics?.variabilityIndex ?? input.ride.vi ?? null),
    averageHeartRate: safeNumber(input.ride.averageHeartRate),
    aerobicDecoupling: safeNumber(input.ride.rideAnalytics?.aerobicDecoupling.decouplingPercent ?? null),
    firstHalfPower: half.firstHalfPower,
    secondHalfPower: half.secondHalfPower,
    durability60: null,
    durability90: null,
    durability120: null,
    averageCadence: safeNumber(input.ride.averageCadence),
    cadenceStability: null,
    powerZoneDistribution: input.ride.rideAnalytics?.powerZones ?? [],
    respiratorySummary: {
      available: false,
      sourceType: "none",
      coveragePercent: null,
      averageMinuteVentilation: null,
      maximumMinuteVentilation: null,
      averageBreathingFrequency: null,
      respiratoryDriftPercent: null,
      ventilationToPowerRelationship: null,
      alignmentConfidence: "unavailable"
    },
    thermalSummary: {
      available: false,
      note: "No thermal companion source"
    },
    hydrationSummary: {
      available: false,
      note: "No hydration companion source"
    },
    sourceCoverage: {
      activityCompletenessPercent: 70,
      ridePresent: true,
      respiratoryPresent: false,
      respiratoryUsable: false,
      hydrationPresent: false,
      thermalPresent: false,
      dataDomainCount: 1
    },
    analysisConfidence: {
      overallPercent: confidence === "high" ? 78 : 66,
      rideClassificationPercent: 72,
      enduranceExecutionPercent: 68,
      respiratoryAnalysisPercent: null
    },
    analysisVersion: "historical_batch_analysis.v1",
    analysisVersionHistory: ["historical_batch_analysis.v1"],
    duplicateIdentity: {
      startTimestampMs: Number.isNaN(startTimestampMs) ? null : startTimestampMs,
      durationSeconds,
      distanceMeters,
      sourceFingerprint: `${input.file.name.toLowerCase()}::${input.file.size}::${input.file.lastModified}`,
      stableSourceHash: sourceKey
    },
    sourceProvenance: buildSourceProvenance({
      sourceKey,
      sourceType: input.sourceType,
      fileName: input.file.name,
      fileSizeBytes: input.file.size,
      sourceActivityId,
      importedAt,
      confidence
    }),
    importMetadata: buildImportMetadata({
      sourceType: input.sourceType,
      fileName: input.file.name,
      sourceActivityId,
      importedAt,
      ftp: input.ftp,
      confidence
    }),
    maximumPower: safeNumber(input.ride.maximumPower),
    workKilojoules: safeNumber(input.ride.rideAnalytics?.workKilojoules),
    createdAt: importedAt,
    updatedAt: importedAt
  };
}

function createInitialRow(file: File): HistoricalRideImportRow {
  const recognized = isRecognizedRideFile(file);
  return {
    fileName: file.name,
    fileSizeBytes: file.size,
    recognized,
    sourceType: sourceTypeFromFileName(file.name),
    detectedRideDate: null,
    sport: null,
    durationSeconds: null,
    distanceMeters: null,
    parseStatus: recognized ? "pending" : "rejected",
    duplicateStatus: "not_checked",
    persistenceStatus: recognized ? "pending" : "failed",
    failureType: "none",
    message: recognized ? null : "Unsupported file format. Supported: .fit, .fit.gz",
    duplicateIdentity: null
  };
}

function summarize(rows: HistoricalRideImportRow[]): HistoricalRideImportSummary {
  return {
    filesScanned: rows.length,
    filesRecognized: rows.filter(row => row.recognized).length,
    filesRejected: rows.filter(row => row.parseStatus === "rejected").length,
    duplicatesDetected: rows.filter(row => row.duplicateStatus === "duplicate" || row.duplicateStatus === "canonical_match").length,
    importsCompleted: rows.filter(row => row.persistenceStatus === "saved").length,
    importsFailed: rows.filter(row => row.persistenceStatus === "failed").length,
    ridesCreated: rows.filter(row => row.persistenceStatus === "saved" && row.duplicateStatus === "not_duplicate").length,
    canonicalMatches: rows.filter(row => row.duplicateStatus === "canonical_match").length,
    provenanceMerges: rows.filter(row => row.duplicateIdentity?.mergedWithExisting).length,
    failedParses: rows.filter(row => row.failureType === "parse_failed").length,
    cancelled: rows.filter(row => row.failureType === "cancelled").length,
    persistenceFailures: rows.filter(row => row.failureType === "persistence_failed").length
  };
}

function emitProgress(input: {
  index: number;
  totalFiles: number;
  filePercent: number;
  stage: HistoricalFitImportWorkerStage | "finalizing" | "persisting" | "cancelled";
  status: string;
  workerDiagnostics: HistoricalFitWorkerDiagnostics;
  rows: HistoricalRideImportRow[];
  row: HistoricalRideImportRow;
  performanceDiagnostics?: HistoricalFitImportPerformanceDiagnostics | null;
  onProgress?: (progress: HistoricalRideImportProgress) => void;
}): HistoricalRideImportSummary {
  const summary = summarize(input.rows);
  input.onProgress?.({
    currentFileIndex: input.index + 1,
    totalFiles: input.totalFiles,
    percent: computeOverallPercent(input.index, input.totalFiles, input.filePercent),
    stage: input.stage,
    status: input.status,
    workerDiagnostics: input.workerDiagnostics,
    rows: [...input.rows],
    row: { ...input.row },
    summary
  });
  return summary;
}

function classifyPerformanceDiagnostics(totalElapsedMs: number, largestStageMs: number | null): HistoricalFitImportPerformanceDiagnostics["classification"] {
  if (largestStageMs !== null && largestStageMs >= 45_000) return "very_slow";
  if (totalElapsedMs >= 45_000) return "very_slow";
  if (largestStageMs !== null && largestStageMs >= 20_000) return "slow";
  if (totalElapsedMs >= 20_000) return "slow";
  return "normal";
}

function buildPerformanceDiagnostics(input: {
  file: File;
  workerPerformanceDiagnostics: HistoricalFitImportPerformanceDiagnostics | null | undefined;
  mainThreadElapsedMs: number;
  workerElapsedMs: number;
}): HistoricalFitImportPerformanceDiagnostics | null {
  const workerPerformance = input.workerPerformanceDiagnostics ?? {
    classification: "normal",
    totalElapsedMs: Math.max(input.workerElapsedMs, input.mainThreadElapsedMs, 0),
    workerElapsedMs: Math.max(input.workerElapsedMs, 0),
    mainThreadPostWorkerElapsedMs: Math.max(input.mainThreadElapsedMs, 0),
    workerConstructionMs: null,
    workerReadyLatencyMs: null,
    mainThreadReadMs: null,
    mainThreadToWorkerMs: null,
    mainThreadFileReadMs: null,
    mainThreadToWorkerPostMs: null,
    workerReceiveLatencyMs: null,
    fitDecodeMs: null,
    normalizationMs: null,
    analyticsMs: null,
    workerResultTransferMs: null,
    persistenceMs: null,
    historyRefreshMs: null,
    fileBytes: input.file.size,
    throughputMbPerSecond: null,
    recordsPerSecond: null,
    largestStageLabel: null,
    largestStagePercent: null,
    stages: {
      mainThreadFileReadMs: null,
      mainThreadToWorkerPostMs: null,
      workerReceiveLatencyMs: null,
      fileAcquisitionToWorkerMs: null,
      decompressionMs: null,
      fitDecodeMs: null,
      normalizationMs: null,
      historicalFtpResolutionMs: null,
      analyticsMs: null,
      workerResultTransferMs: null,
      serializationMs: null,
      canonicalDuplicateMatchingMs: null,
      persistenceMs: null,
      historyRefreshMs: null,
      athleteHistoryIndexRefreshMs: null,
      uiFinalizationMs: null
    } satisfies HistoricalFitImportWorkerStageTimings,
    workerStages: {
      workerStartupMs: null,
      startImportReceiptToParseStartMs: null,
      fitDecodeMs: null,
      parseCallbackAndRecordCollectionMs: null,
      normalizationMs: null,
      analyticsMs: null,
      serializationMs: null,
      parserInvocationKind: "unknown"
    } satisfies HistoricalFitImportWorkerInternalTimings,
    counts: {
      fitMessageCount: null,
      parsedStructureNodeCount: null,
      fitRecordCount: null,
      sessionCount: null,
      normalizedSamplesProduced: null,
      timeSeriesPointsProduced: null,
      analysisMetricCount: null
    } satisfies HistoricalFitImportWorkerCounts
  };

  const stageValues = Object.entries(workerPerformance.stages)
    .filter(([, value]) => typeof value === "number" && value !== null)
    .map(([label, value]) => ({ label, value: value as number }));
  const largestStage = stageValues.reduce<{ label: string; value: number } | null>((current, candidate) => {
    if (!current || candidate.value > current.value) {
      return { label: candidate.label, value: candidate.value };
    }
    return current;
  }, null);

  const totalElapsedMs = Math.max(workerPerformance.totalElapsedMs, input.workerElapsedMs, input.mainThreadElapsedMs);
  const largestStageMs = largestStage?.value ?? null;
  const largestStagePercent = largestStageMs !== null && totalElapsedMs > 0 ? Math.round((largestStageMs / totalElapsedMs) * 100) : null;
  const throughputMbPerSecond = input.file.size > 0 && totalElapsedMs > 0 ? (input.file.size / (1024 * 1024)) / (totalElapsedMs / 1000) : null;
  const recordsPerSecond = workerPerformance.counts.fitRecordCount && totalElapsedMs > 0 ? workerPerformance.counts.fitRecordCount / (totalElapsedMs / 1000) : null;

  return {
    ...workerPerformance,
    classification: classifyPerformanceDiagnostics(totalElapsedMs, largestStageMs),
    totalElapsedMs,
    workerElapsedMs: Math.max(workerPerformance.workerElapsedMs, input.workerElapsedMs),
    mainThreadPostWorkerElapsedMs: Math.max(workerPerformance.mainThreadPostWorkerElapsedMs, input.mainThreadElapsedMs),
    workerConstructionMs: workerPerformance.workerConstructionMs ?? null,
    workerReadyLatencyMs: workerPerformance.workerReadyLatencyMs ?? null,
    mainThreadReadMs: workerPerformance.mainThreadReadMs ?? null,
    mainThreadToWorkerMs: workerPerformance.mainThreadToWorkerMs ?? null,
    mainThreadFileReadMs: workerPerformance.mainThreadFileReadMs ?? null,
    mainThreadToWorkerPostMs: workerPerformance.mainThreadToWorkerPostMs ?? null,
    workerReceiveLatencyMs: workerPerformance.workerReceiveLatencyMs ?? null,
    fitDecodeMs: workerPerformance.fitDecodeMs ?? workerPerformance.stages.fitDecodeMs ?? null,
    normalizationMs: workerPerformance.normalizationMs ?? workerPerformance.stages.normalizationMs ?? null,
    analyticsMs: workerPerformance.analyticsMs ?? workerPerformance.stages.analyticsMs ?? null,
    workerResultTransferMs: workerPerformance.workerResultTransferMs ?? workerPerformance.stages.workerResultTransferMs ?? null,
    persistenceMs: workerPerformance.persistenceMs ?? workerPerformance.stages.persistenceMs ?? null,
    historyRefreshMs: workerPerformance.historyRefreshMs ?? workerPerformance.stages.historyRefreshMs ?? null,
    fileBytes: input.file.size,
    throughputMbPerSecond,
    recordsPerSecond,
    largestStageLabel: largestStage?.label ?? null,
    largestStagePercent,
    stages: {
      ...workerPerformance.stages,
      uiFinalizationMs: workerPerformance.stages.uiFinalizationMs ?? input.mainThreadElapsedMs
    }
  };
}

export async function importHistoricalRideBatch(
  files: File[],
  athleteProfile: AthleteProfile,
  options: ImportOptions = {}
): Promise<HistoricalRideImportResult> {
  const persistRecord = options.persistRecord ?? saveCompletedActivityRecord;
  const deleteRecord = options.deleteRecord ?? deleteCompletedActivityHistoryRecord;
  const selectionReceivedAtEpochMs = options.selectionReceivedAtEpochMs ?? epochNowMs();
  const workerClient = options.createWorkerClient?.() ?? createHistoricalFitImportWorkerClient();
  const rows = files.map(createInitialRow);
  const currentDiagnostics: HistoricalFitWorkerDiagnostics = {
    stage: "queued",
    workerState: "idle",
    lastProgressAt: null,
    performanceDiagnostics: null,
    handoffProfile: null
  };

  // Profiling instrumentation must never be able to fail an import or corrupt history.
  const safeHandoffProfile = (): WorkerHandoffProfile | null => {
    try {
      return workerClient.getHandoffProfile?.() ?? null;
    } catch {
      return null;
    }
  };

  const safeApplyHandoffMarks = (patch: Partial<WorkerHandoffMarks>): WorkerHandoffProfile | null => {
    try {
      return workerClient.applyHandoffMarks?.(patch) ?? null;
    } catch {
      return null;
    }
  };

  const syncDiagnostics = (next: HistoricalFitWorkerDiagnostics): HistoricalFitWorkerDiagnostics => {
    currentDiagnostics.stage = next.stage;
    currentDiagnostics.workerState = next.workerState;
    currentDiagnostics.lastProgressAt = next.lastProgressAt;
    currentDiagnostics.performanceDiagnostics = next.performanceDiagnostics ?? currentDiagnostics.performanceDiagnostics ?? null;
    currentDiagnostics.handoffProfile = next.handoffProfile ?? safeHandoffProfile() ?? currentDiagnostics.handoffProfile ?? null;
    return { ...currentDiagnostics };
  };

  const onAbort = () => {
    workerClient.cancel();
  };
  options.signal?.addEventListener("abort", onAbort);
  try {
    if (rows.length > 0) {
      emitProgress({
        index: 0,
        totalFiles: rows.length,
        filePercent: 1,
        stage: "queued",
        status: "Queued for historical import",
        workerDiagnostics: { ...currentDiagnostics },
        rows,
        row: rows[0],
        onProgress: options.onProgress
      });
      await yieldToUi();
    }

    let latestPerformanceDiagnostics: HistoricalFitImportPerformanceDiagnostics | null = null;

    for (let index = 0; index < rows.length; index += 1) {
      const row = rows[index];
      const file = files[index];
      let currentPerformanceDiagnostics: HistoricalFitImportPerformanceDiagnostics | null = null;
      const importStartAt = performance.now();
      let workerTransferElapsedMs: number | null = null;
      let historicalFtpResolutionElapsedMs: number | null = null;
      let persistenceElapsedMs: number | null = null;
      let historyRefreshElapsedMs: number | null = null;
      let uiFinalizationElapsedMs: number | null = null;

      if (options.signal?.aborted) {
        if (row.parseStatus === "pending") {
          row.parseStatus = "cancelled";
        }
        row.duplicateStatus = "not_checked";
        row.persistenceStatus = "cancelled";
        row.failureType = "cancelled";
        row.message = "Import cancelled by user.";
        const summary = emitProgress({
          index,
          totalFiles: rows.length,
          filePercent: 100,
          stage: "cancelled",
          status: "Import cancelled by user.",
          workerDiagnostics: syncDiagnostics({ ...currentDiagnostics, workerState: "cancelled", lastProgressAt: new Date().toISOString() }),
          rows,
          row,
          onProgress: options.onProgress
        });
        throw new HistoricalRideImportCancelledError({
          rows: [...rows],
          summary
        });
      }

      if (!row.recognized) {
        emitProgress({
          index,
          totalFiles: rows.length,
          filePercent: 100,
          stage: "complete",
          status: row.message ?? "Unsupported file type",
          workerDiagnostics: { ...currentDiagnostics },
          rows,
          row,
          onProgress: options.onProgress
        });
        continue;
      }

      try {
        row.failureType = "none";
        row.message = "Preparing import";
        emitProgress({
          index,
          totalFiles: rows.length,
          filePercent: 5,
          stage: "queued",
          status: "Preparing import",
          workerDiagnostics: syncDiagnostics({ ...currentDiagnostics, stage: "queued", workerState: "starting", lastProgressAt: new Date().toISOString() }),
          rows,
          row,
          onProgress: options.onProgress
        });

        await workerClient.prepare?.();
        const workerTransferStartAt = performance.now();
        const parsed = await workerClient.startImport({
          file,
          athleteProfile,
          selectionReceivedAtEpochMs: selectionReceivedAtEpochMs ?? undefined,
          onDiagnostics: diagnostics => {
            syncDiagnostics(diagnostics);
          },
          onProgress: (workerProgress: HistoricalFitImportWorkerProgress, diagnostics) => {
            row.message = workerProgress.status;
            const stagePercent = toFilePercent(workerProgress.stage, workerProgress.percent);
            currentPerformanceDiagnostics = diagnostics.performanceDiagnostics ?? currentPerformanceDiagnostics;
            emitProgress({
              index,
              totalFiles: rows.length,
              filePercent: stagePercent,
              stage: workerProgress.stage,
              status: workerProgress.status,
              workerDiagnostics: syncDiagnostics(diagnostics),
              rows,
              row,
              onProgress: options.onProgress
            });
          }
        });

        throwIfAborted(options.signal);

        workerTransferElapsedMs = Math.max(0, performance.now() - workerTransferStartAt);
        currentPerformanceDiagnostics = currentPerformanceDiagnostics ?? parsed.performanceDiagnostics ?? null;
        const workerElapsedMs = performance.now() - importStartAt;
        const rideDateIso = toIso(parsed.ride.rideDate) ?? new Date().toISOString();
        const historicalFtpResolutionStartAt = performance.now();
        const historicalFtp = parsed.historicalFtp ?? selectFtpForHistoricalRide(athleteProfile, inferRideSurfaceType(file.name), rideDateIso);
        historicalFtpResolutionElapsedMs = Math.max(0, performance.now() - historicalFtpResolutionStartAt);

        row.message = "Finalizing parsed ride";
        const finalizationStartAt = performance.now();
        emitProgress({
          index,
          totalFiles: rows.length,
          filePercent: 88,
          stage: "finalizing",
          status: "Finalizing parsed ride",
          workerDiagnostics: syncDiagnostics({ ...currentDiagnostics, workerState: "completed", stage: "complete", lastProgressAt: new Date().toISOString() }),
          rows,
          row,
          onProgress: options.onProgress
        });

        throwIfAborted(options.signal);

        const record = buildHistoricalRecord({
          ride: parsed.ride,
          ftp: historicalFtp,
          file,
          sourceType: row.sourceType
        });

        throwIfAborted(options.signal);
        row.message = "Saving ride history";
        emitProgress({
          index,
          totalFiles: rows.length,
          filePercent: 92,
          stage: "persisting",
          status: "Saving ride history",
          workerDiagnostics: { ...currentDiagnostics },
          rows,
          row,
          onProgress: options.onProgress
        });

        await options.onBeforePersist?.({ file, row: { ...row } });
        throwIfAborted(options.signal);

        // Transaction boundary: no persistence occurs before worker SUCCESS and final cancellation checks.
        row.parseStatus = "parsed";
        const persistenceStartAt = performance.now();
        safeApplyHandoffMarks({ persistenceStartedAtEpochMs: epochNowMs() });
        const saveResult = await persistRecord(record);
        persistenceElapsedMs = Math.max(0, performance.now() - persistenceStartAt);
        syncDiagnostics({
          ...currentDiagnostics,
          handoffProfile: safeApplyHandoffMarks({ persistenceCompletedAtEpochMs: epochNowMs() }) ?? currentDiagnostics.handoffProfile ?? null
        });

        // If cancellation is requested while persistence is in flight, remove the just-written record.
        if (options.signal?.aborted) {
          if (!saveResult.duplicateIdentity.mergedWithExisting) {
            await deleteRecord(saveResult.record.activityId);
          }
          row.parseStatus = "cancelled";
          row.persistenceStatus = "cancelled";
          row.failureType = "cancelled";
          row.message = "Import cancelled by user.";
          const summary = emitProgress({
            index,
            totalFiles: rows.length,
            filePercent: 100,
            stage: "cancelled",
            status: "Import cancelled by user.",
            workerDiagnostics: syncDiagnostics({ ...currentDiagnostics, workerState: "cancelled", lastProgressAt: new Date().toISOString() }),
            rows,
            row,
            onProgress: options.onProgress
          });
          throw new HistoricalRideImportCancelledError({
            rows: [...rows],
            summary
          });
        }

        const historyRefreshStartAt = performance.now();
        row.detectedRideDate = rideDateIso;
        row.sport = detectSport(parsed.diagnostics);
        row.durationSeconds = record.durationSeconds;
        row.distanceMeters = record.distanceMeters;
        row.duplicateIdentity = saveResult.duplicateIdentity;

        historyRefreshElapsedMs = Math.max(0, performance.now() - historyRefreshStartAt);
        const mainThreadElapsedMs = performance.now() - importStartAt;
        currentPerformanceDiagnostics = buildPerformanceDiagnostics({
          file,
          workerPerformanceDiagnostics: currentPerformanceDiagnostics ?? parsed.performanceDiagnostics ?? null,
          mainThreadElapsedMs,
          workerElapsedMs
        });
        currentPerformanceDiagnostics = currentPerformanceDiagnostics ? {
          ...currentPerformanceDiagnostics,
          mainThreadFileReadMs: currentPerformanceDiagnostics.mainThreadFileReadMs ?? null,
          mainThreadToWorkerPostMs: currentPerformanceDiagnostics.mainThreadToWorkerPostMs ?? null,
          workerReceiveLatencyMs: currentPerformanceDiagnostics.workerReceiveLatencyMs ?? null,
          fitDecodeMs: currentPerformanceDiagnostics.fitDecodeMs ?? currentPerformanceDiagnostics.stages.fitDecodeMs ?? null,
          normalizationMs: currentPerformanceDiagnostics.normalizationMs ?? currentPerformanceDiagnostics.stages.normalizationMs ?? null,
          analyticsMs: currentPerformanceDiagnostics.analyticsMs ?? currentPerformanceDiagnostics.stages.analyticsMs ?? null,
          workerResultTransferMs: currentPerformanceDiagnostics.workerResultTransferMs ?? currentPerformanceDiagnostics.stages.workerResultTransferMs ?? null,
          persistenceMs: currentPerformanceDiagnostics.persistenceMs ?? currentPerformanceDiagnostics.stages.persistenceMs ?? null,
          historyRefreshMs: currentPerformanceDiagnostics.historyRefreshMs ?? currentPerformanceDiagnostics.stages.historyRefreshMs ?? null,
          stages: {
            ...currentPerformanceDiagnostics.stages,
            mainThreadFileReadMs: currentPerformanceDiagnostics.stages.mainThreadFileReadMs ?? currentPerformanceDiagnostics.mainThreadFileReadMs ?? null,
            mainThreadToWorkerPostMs: currentPerformanceDiagnostics.stages.mainThreadToWorkerPostMs ?? currentPerformanceDiagnostics.mainThreadToWorkerPostMs ?? null,
            workerReceiveLatencyMs: currentPerformanceDiagnostics.stages.workerReceiveLatencyMs ?? currentPerformanceDiagnostics.workerReceiveLatencyMs ?? null,
            fileAcquisitionToWorkerMs: currentPerformanceDiagnostics.stages.fileAcquisitionToWorkerMs ?? currentPerformanceDiagnostics.workerReceiveLatencyMs ?? null,
            serializationMs: currentPerformanceDiagnostics.stages.serializationMs ?? null,
            historicalFtpResolutionMs: currentPerformanceDiagnostics.stages.historicalFtpResolutionMs ?? historicalFtpResolutionElapsedMs,
            persistenceMs: currentPerformanceDiagnostics.stages.persistenceMs ?? persistenceElapsedMs,
            historyRefreshMs: currentPerformanceDiagnostics.stages.historyRefreshMs ?? historyRefreshElapsedMs,
            athleteHistoryIndexRefreshMs: currentPerformanceDiagnostics.stages.athleteHistoryIndexRefreshMs ?? historyRefreshElapsedMs,
            uiFinalizationMs: currentPerformanceDiagnostics.stages.uiFinalizationMs ?? Math.max(0, finalizationStartAt ? performance.now() - finalizationStartAt : 0)
          }
        } : null;

        if (saveResult.duplicateIdentity.mergedWithExisting && saveResult.duplicateIdentity.strategy === "canonical_match") {
          row.duplicateStatus = "canonical_match";
          row.persistenceStatus = "skipped_duplicate";
          row.message = "Merged with canonical historical activity (high-confidence match).";
        } else if (saveResult.duplicateIdentity.mergedWithExisting) {
          row.duplicateStatus = "duplicate";
          row.persistenceStatus = "skipped_duplicate";
          row.message = "Duplicate detected; existing activity retained with merged provenance.";
        } else {
          row.duplicateStatus = "not_duplicate";
          row.persistenceStatus = "saved";
          row.message = historicalFtp.fallbackUsed
            ? "Imported with FTP fallback policy; confidence reduced."
            : "Imported successfully.";
        }
      } catch (error) {
        if (options.signal?.aborted || isAbortLikeError(error) || isWorkerCancelledError(error)) {
          if (row.parseStatus === "pending") {
            row.parseStatus = "cancelled";
          }
          row.duplicateStatus = "not_checked";
          row.persistenceStatus = "cancelled";
          row.failureType = "cancelled";
          row.message = "Import cancelled by user.";

          const summary = emitProgress({
            index,
            totalFiles: rows.length,
            filePercent: 100,
            stage: "cancelled",
            status: "Import cancelled by user.",
            workerDiagnostics: syncDiagnostics({ ...currentDiagnostics, workerState: "cancelled", lastProgressAt: new Date().toISOString() }),
            rows,
            row,
            onProgress: options.onProgress
          });

          throw new HistoricalRideImportCancelledError({
            rows: [...rows],
            summary
          });
        }

        const wasParsedBeforeFailure = row.parseStatus === "parsed";
        row.parseStatus = wasParsedBeforeFailure ? "parsed" : "failed";
        row.duplicateStatus = "not_checked";
        row.persistenceStatus = "failed";
        row.failureType = wasParsedBeforeFailure ? "persistence_failed" : "parse_failed";
        const workerError = error instanceof HistoricalFitImportWorkerClientError ? error : null;
        row.message = workerError?.details?.message ?? (error instanceof Error ? error.message : "Failed to parse file.");
      }

      const finalUiElapsedMs = performance.now() - importStartAt;
      uiFinalizationElapsedMs = Math.max(0, finalUiElapsedMs - (currentPerformanceDiagnostics?.workerElapsedMs ?? 0));
      if (currentPerformanceDiagnostics) {
        currentPerformanceDiagnostics = {
          ...currentPerformanceDiagnostics,
          totalElapsedMs: Math.max(currentPerformanceDiagnostics.totalElapsedMs, finalUiElapsedMs),
          mainThreadPostWorkerElapsedMs: Math.max(currentPerformanceDiagnostics.mainThreadPostWorkerElapsedMs, uiFinalizationElapsedMs),
          stages: {
            ...currentPerformanceDiagnostics.stages,
            mainThreadFileReadMs: currentPerformanceDiagnostics.stages.mainThreadFileReadMs ?? currentPerformanceDiagnostics.mainThreadFileReadMs ?? null,
            mainThreadToWorkerPostMs: currentPerformanceDiagnostics.stages.mainThreadToWorkerPostMs ?? currentPerformanceDiagnostics.mainThreadToWorkerPostMs ?? null,
            workerReceiveLatencyMs: currentPerformanceDiagnostics.stages.workerReceiveLatencyMs ?? currentPerformanceDiagnostics.workerReceiveLatencyMs ?? null,
            fileAcquisitionToWorkerMs: currentPerformanceDiagnostics.stages.fileAcquisitionToWorkerMs ?? currentPerformanceDiagnostics.workerReceiveLatencyMs ?? null,
            serializationMs: currentPerformanceDiagnostics.stages.serializationMs ?? null,
            historicalFtpResolutionMs: currentPerformanceDiagnostics.stages.historicalFtpResolutionMs ?? historicalFtpResolutionElapsedMs,
            persistenceMs: currentPerformanceDiagnostics.stages.persistenceMs ?? persistenceElapsedMs,
            historyRefreshMs: currentPerformanceDiagnostics.stages.historyRefreshMs ?? historyRefreshElapsedMs,
            athleteHistoryIndexRefreshMs: currentPerformanceDiagnostics.stages.athleteHistoryIndexRefreshMs ?? historyRefreshElapsedMs,
            uiFinalizationMs: currentPerformanceDiagnostics.stages.uiFinalizationMs ?? uiFinalizationElapsedMs
          }
        };
      }
      latestPerformanceDiagnostics = currentPerformanceDiagnostics ?? latestPerformanceDiagnostics;

      emitProgress({
        index,
        totalFiles: rows.length,
        filePercent: 100,
        stage: "complete",
        status: row.message ?? "Import complete",
        workerDiagnostics: { ...currentDiagnostics },
        rows,
        row,
        performanceDiagnostics: currentPerformanceDiagnostics,
        onProgress: options.onProgress
      });

      await yieldToUi();
    }

    return {
      rows,
      summary: summarize(rows),
      performanceDiagnostics: latestPerformanceDiagnostics
    };
  } finally {
    options.signal?.removeEventListener("abort", onAbort);
  }
}
